import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";
import styles from "./Capabilities.module.scss";

const capabilities = [
  {
    id: "command-center",
    title: "A command center for your day",
    description:
      "Today, Tomorrow, Week ahead, Time travel, and Coach — Vertik turns meetings, follow-ups, hot leads, deal risks, and overdue tasks into one clear plan, ranked by impact.",
    layout: "half",
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <rect x="3" y="3" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5" />
        <rect x="11" y="3" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5" />
        <rect x="3" y="11" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5" />
        <rect x="11" y="11" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5" />
      </svg>
    ),
  },
  {
    id: "inbox",
    title: "An inbox that understands sales",
    description:
      "It knows a lead from a vendor, summarizes the thread, links it to CRM, detects intent, and drafts the reply.",
    layout: "half",
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path
          d="M3 5H17V15H3V5Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
        <path
          d="M3 5L10 11L17 5"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    id: "deal-health",
    title: "Deal & account health",
    description:
      "Continuously reads CRM, email, and meeting signals to flag at-risk deals, quiet accounts, and hot leads before they slip.",
    layout: "third",
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path
          d="M10 17C10 17 3 12 3 7.5C3 5 5 3 7.5 3C8.8 3 10 3.6 10 5C10 3.6 11.2 3 12.5 3C15 3 17 5 17 7.5C17 12 10 17 10 17Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
        <path
          d="M6 10H8L9 8L11 13L12 11H14"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    id: "meetings",
    title: "Every meeting becomes a plan",
    description:
      "Walk in with the account brief, stakeholders, and questions. Walk out with the recap, CRM updates, and follow-ups ready.",
    layout: "third",
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <rect x="3" y="4" width="14" height="13" rx="2" stroke="currentColor" strokeWidth="1.5" />
        <path d="M3 8H17" stroke="currentColor" strokeWidth="1.5" />
        <path d="M7 2V5M13 2V5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M7 12L9 14L13 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    id: "tasks",
    title: "Tasks that are revenue actions",
    description:
      "Created from emails, meetings, and deal risk — each one arrives with the full context to make the right call and act on the spot.",
    layout: "third",
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path d="M6 6H16M6 10H16M6 14H12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        <circle cx="4" cy="6" r="1" fill="currentColor" />
        <circle cx="4" cy="10" r="1" fill="currentColor" />
        <path d="M14 14L16 16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    id: "coach",
    title: "A coach that learns how you sell",
    description:
      "Vertik spots patterns in follow-up discipline, meeting prep, objection handling, and response time — then coaches inside the work, not at the next pipeline review.",
    layout: "wide",
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path d="M3 8L10 4L17 8V14C17 14.55 16.55 15 16 15H4C3.45 15 3 14.55 3 14V8Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
        <path d="M7 15V11H13V15" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    id: "chat",
    title: "Ask anything. Do anything.",
    description:
      "\"Which deals will slip this week?\" \"Draft a reply.\" \"Create follow-ups.\" One sales chat that acts.",
    layout: "compact",
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path
          d="M4 4H16V12H8L4 16V12H4V4Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
        <path d="M7 8H13M7 11H11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
  },
];

const layoutClassMap = {
  half: styles.cardHalf,
  third: styles.cardThird,
  wide: styles.cardWide,
  compact: styles.cardCompact,
};

const Capabilities = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.1 });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.07, delayChildren: 0.08 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 24 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" },
    },
  };

  return (
    <motion.section
      id="capabilities"
      ref={ref}
      className={styles.section}
      variants={containerVariants}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
    >
      <div className="container-custom">
        <motion.div className={styles.badge} variants={itemVariants}>
          <span className={styles.badgeDot} aria-hidden="true" />
          One Companion
        </motion.div>

        <motion.h2 className={styles.headline} variants={itemVariants}>
          Every part of the sales day — handled by one Chief of Staff
        </motion.h2>

        <motion.p className={styles.lead} variants={itemVariants}>
          Not a set of features to learn. One companion that already knows your
          day and works across all of it — always in context.
        </motion.p>

        <motion.div className={styles.grid} variants={itemVariants}>
          {capabilities.map((capability) => (
            <motion.article
              key={capability.id}
              className={`${styles.card} ${layoutClassMap[capability.layout]}`}
              data-ui="capability-card"
              whileHover={{ y: -4 }}
              transition={{ duration: 0.25 }}
            >
              <span className={styles.cardIcon}>{capability.icon}</span>
              <h3 className={styles.cardTitle}>{capability.title}</h3>
              <p className={styles.cardDescription}>{capability.description}</p>
            </motion.article>
          ))}
        </motion.div>
      </div>
    </motion.section>
  );
};

export default Capabilities;
